import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { browseCategoriesForDropdown, formatNameWithCount } from '../categoryLabels';

const fmt = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

function clamp(n, lo, hi) {
  return Math.min(Math.max(n, lo), hi);
}

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

const SUBCATEGORY_VISIBLE_COUNT = 5;

function scrollPanelToElement(panel, element) {
  if (!panel || !element) {
    return;
  }
  const panelRect = panel.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const relativeTop = elementRect.top - panelRect.top + panel.scrollTop;
  const padding = 12;
  const maxScroll = Math.max(0, panel.scrollHeight - panel.clientHeight);
  panel.scrollTop = clamp(relativeTop - padding, 0, maxScroll);
}

export function groupRetailersByCategory(retailers, browseCategories) {
  const retailerSet = new Set(retailers);
  const assigned = new Set();
  const groups = [];

  for (const category of browseCategories) {
    if (category.comingSoon) {
      continue;
    }
    const names = (category.retailers ?? [])
      .filter((name) => retailerSet.has(name))
      .sort((a, b) => a.localeCompare(b));
    if (names.length === 0) {
      continue;
    }
    names.forEach((name) => assigned.add(name));
    groups.push({ categoryName: category.name, retailers: names });
  }

  const unassigned = retailers
    .filter((name) => !assigned.has(name))
    .sort((a, b) => a.localeCompare(b));
  if (unassigned.length > 0) {
    groups.push({ categoryName: 'Other', retailers: unassigned });
  }

  return groups;
}

export function parseScopedSearchQuery(
  effectiveQuery,
  browseCategories,
  shopSlug,
) {
  if (!shopSlug) {
    return { userQuery: effectiveQuery, subcategory: null };
  }
  const category = browseCategories.find((c) => c.slug === shopSlug);
  if (!category) {
    return { userQuery: effectiveQuery, subcategory: null };
  }
  const subcategories = Array.isArray(category.subcategories)
    ? category.subcategories
    : [];
  const trimmed = effectiveQuery.trim();

  for (const sub of subcategories) {
    const subSearch = sub.search.trim();
    if (trimmed === subSearch) {
      return { userQuery: '', subcategory: sub };
    }
    const andSuffix = ` AND ${subSearch}`;
    if (trimmed.endsWith(andSuffix)) {
      return {
        userQuery: trimmed.slice(0, trimmed.length - andSuffix.length),
        subcategory: sub,
      };
    }
  }

  return { userQuery: effectiveQuery, subcategory: null };
}

export function findActiveSubcategory(browseCategories, shopSlug, query) {
  return parseScopedSearchQuery(query, browseCategories, shopSlug).subcategory;
}

/**
 * Text for the search input. Strips scoped subcategory suffix when recognized.
 * Returns undefined while a shop-scoped query is still ambiguous (categories loading).
 *
 * @param {string} effectiveQuery
 * @param {BrowseCategory[]} browseCategories
 * @param {string} shopSlug
 * @param {{ search?: string } | null | undefined} [subcategoryHint]
 * @returns {string | undefined}
 */
export function resolveSearchBoxQuery(
  effectiveQuery,
  browseCategories,
  shopSlug,
  subcategoryHint = null,
) {
  const parsed = parseScopedSearchQuery(effectiveQuery, browseCategories, shopSlug);
  if (parsed.subcategory) {
    return parsed.userQuery;
  }

  const hintSearch =
    typeof subcategoryHint?.search === 'string' ? subcategoryHint.search.trim() : '';
  if (hintSearch !== '') {
    const trimmed = effectiveQuery.trim();
    if (trimmed === hintSearch) {
      return '';
    }
    const andSuffix = ` AND ${hintSearch}`;
    if (trimmed.endsWith(andSuffix)) {
      return trimmed.slice(0, trimmed.length - andSuffix.length);
    }
  }

  if (shopSlug && browseCategories.length === 0 && effectiveQuery.includes(' AND ')) {
    return undefined;
  }

  return parsed.userQuery;
}

function CategoryFilterGroup({
  category,
  activeShopCategorySlug,
  activeSubcategorySearch,
  expanded,
  forceExpanded,
  onToggleExpanded,
  onShopCategoryChange,
  onSubcategoryChange,
  onClose,
  scrollTargetRef,
}) {
  const subcategories = Array.isArray(category.subcategories)
    ? category.subcategories
    : [];
  const showAllSubcategories = forceExpanded || expanded;
  const hasHiddenSubcategories =
    subcategories.length > SUBCATEGORY_VISIBLE_COUNT;
  const visibleSubcategories = showAllSubcategories
    ? subcategories
    : subcategories.slice(0, SUBCATEGORY_VISIBLE_COUNT);
  const categorySelected =
    activeShopCategorySlug === category.slug && activeSubcategorySearch === null;
  const isActiveCategory = activeShopCategorySlug === category.slug;

  return (
    <li
      className="filter-check-list__group"
      ref={
        isActiveCategory && activeSubcategorySearch === null
          ? scrollTargetRef
          : undefined
      }
    >
      <label className="filter-check-list__label filter-check-list__label--category">
        <input
          type="radio"
          name="serp-shop-category"
          checked={categorySelected}
          disabled={category.comingSoon}
          onChange={() => {
            if (category.comingSoon) {
              return;
            }
            onShopCategoryChange(category.slug);
            onClose();
          }}
        />
        <span>{formatNameWithCount(category.name, category.count)}</span>
      </label>
      {subcategories.length > 0 && !category.comingSoon && (
        <ul className="filter-check-list__sub-list">
          {visibleSubcategories.map((sub) => (
            <li
              key={sub.name}
              className="filter-check-list__sub-item"
              ref={
                isActiveCategory && activeSubcategorySearch === sub.search
                  ? scrollTargetRef
                  : undefined
              }
            >
              <label className="filter-check-list__label filter-check-list__label--sub">
                <input
                  type="radio"
                  name="serp-shop-category"
                  checked={
                    activeShopCategorySlug === category.slug
                    && activeSubcategorySearch === sub.search
                  }
                  onChange={() => {
                    onSubcategoryChange(category.slug, sub.search);
                    onClose();
                  }}
                />
                <span>{formatNameWithCount(sub.name, sub.count)}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {hasHiddenSubcategories && !category.comingSoon && !forceExpanded && (
        <button
          type="button"
          className="filter-check-list__expand"
          aria-expanded={expanded}
          onClick={onToggleExpanded}
        >
          {expanded
            ? 'Show less'
            : `Show ${subcategories.length - SUBCATEGORY_VISIBLE_COUNT} more`}
        </button>
      )}
    </li>
  );
}

function BreadcrumbSep() {
  return (
    <span className="category-breadcrumbs__sep" aria-hidden="true">
      ›
    </span>
  );
}

function BreadcrumbDropdownIcon() {
  return (
    <span className="category-breadcrumbs__dropdown-icon" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path
          d="M5 7.5L10 12.5L15 7.5"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function FilterCheckboxPill({ checked, onChange, children }) {
  return (
    <label
      className={`filter-checkbox-pill${checked ? ' filter-checkbox-pill--active' : ''}`}
    >
      <input
        type="checkbox"
        className="filter-checkbox-pill__input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="filter-checkbox-pill__check" aria-hidden="true">
        {checked && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2.5 6L5 8.5L9.5 3.5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className="filter-checkbox-pill__text">{children}</span>
    </label>
  );
}

export function FilterViewToggle({ viewMode, onViewModeChange, className = '' }) {
  return (
    <div
      className={`filter-view-toggle${className ? ` ${className}` : ''}`}
      role="group"
      aria-label="Product layout"
    >
      <button
        type="button"
        className={`filter-view-toggle__btn${viewMode === 'grid' ? ' filter-view-toggle__btn--active' : ''}`}
        onClick={() => onViewModeChange('grid')}
        aria-pressed={viewMode === 'grid'}
        aria-label="Grid view"
      >
        <span className="filter-view-toggle__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1 1h6v6H1V1zm8 0h6v6H9V1zM1 9h6v6H1V9zm8 0h6v6H9V9z" />
          </svg>
        </span>
        <span className="filter-view-toggle__label">Grid</span>
      </button>
      <button
        type="button"
        className={`filter-view-toggle__btn${viewMode === 'list' ? ' filter-view-toggle__btn--active' : ''}`}
        onClick={() => onViewModeChange('list')}
        aria-pressed={viewMode === 'list'}
        aria-label="List view"
      >
        <span className="filter-view-toggle__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 3h12v1.5H2V3zm0 4h12v1.5H2V7zm0 4h12v1.5H2V11z" />
          </svg>
        </span>
        <span className="filter-view-toggle__label">List</span>
      </button>
    </div>
  );
}

export function SearchFiltersBar({
  browseCategories = [],
  activeShopCategorySlug,
  activeQuery = '',
  onShopCategoryChange,
  onSubcategoryChange,
  domainMin,
  domainMax,
  filterMin,
  filterMax,
  onFilterMinChange,
  onFilterMaxChange,
  retailers,
  selectedRetailers,
  onToggleRetailer,
  onlyInStock,
  onOnlyInStockChange,
  viewMode,
  onViewModeChange,
  saleActive = false,
  onSaleChange,
}) {
  const [openId, setOpenId] = useState(null);
  const [expandedCategorySlugs, setExpandedCategorySlugs] = useState(
    () => new Set(),
  );
  const [sliderDraft, setSliderDraft] = useState(null);
  const sliderDraftRef = useRef(null);
  const rootRef = useRef(null);
  const categoryPanelRef = useRef(null);
  const scrollTargetRef = useRef(null);

  useEffect(() => {
    sliderDraftRef.current = sliderDraft;
  }, [sliderDraft]);

  useEffect(() => {
    setSliderDraft(null);
  }, [filterMin, filterMax]);

  useEffect(() => {
    if (sliderDraft == null) {
      return undefined;
    }
    function commitSliderDraft() {
      const draft = sliderDraftRef.current;
      if (!draft) {
        return;
      }
      onFilterMinChange(draft.min);
      onFilterMaxChange(draft.max);
      setSliderDraft(null);
    }
    document.addEventListener('pointerup', commitSliderDraft);
    return () => document.removeEventListener('pointerup', commitSliderDraft);
  }, [sliderDraft != null, onFilterMinChange, onFilterMaxChange]);

  const scopedSearch = useMemo(
    () =>
      parseScopedSearchQuery(
        activeQuery,
        browseCategories,
        activeShopCategorySlug,
      ),
    [activeQuery, browseCategories, activeShopCategorySlug],
  );
  const activeSubcategory = scopedSearch.subcategory;
  const breadcrumbSearchLabel = scopedSearch.userQuery.trim();
  const categoryDropdownOpen = openId === 'category-breadcrumb';

  useEffect(() => {
    if (openId !== 'category-breadcrumb') {
      setExpandedCategorySlugs(new Set());
    }
  }, [openId]);

  useLayoutEffect(() => {
    if (!categoryDropdownOpen || !activeShopCategorySlug) {
      return;
    }
    scrollPanelToElement(categoryPanelRef.current, scrollTargetRef.current);
  }, [
    categoryDropdownOpen,
    activeShopCategorySlug,
    activeSubcategory?.search,
    browseCategories.length,
  ]);

  useEffect(() => {
    function handlePointerDown(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpenId(null);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  const hasDomain = domainMax > domainMin || (domainMin === domainMax && domainMin > 0);
  const safeMin = hasDomain ? domainMin : 0;
  const safeMax = hasDomain ? Math.max(domainMax, domainMin) : 1;
  const step =
    safeMax - safeMin > 50
      ? 1
      : safeMax - safeMin > 10
        ? 0.5
        : 0.01;

  const displayMin = sliderDraft?.min ?? filterMin;
  const displayMax = sliderDraft?.max ?? filterMax;

  const priceActive =
    hasDomain &&
    (roundMoney(displayMin) > roundMoney(safeMin) ||
      roundMoney(displayMax) < roundMoney(safeMax));

  const retailerActive = selectedRetailers.size > 0;

  const retailerGroups = useMemo(
    () => groupRetailersByCategory(retailers, browseCategories),
    [retailers, browseCategories],
  );

  const activeCategory = browseCategories.find(
    (c) => c.slug === activeShopCategorySlug,
  );
  const activeCategoryName = activeCategory?.name;
  const isPriceCollectionScope = activeCategory?.priceCollection === true;
  const dropdownCategories = useMemo(
    () => browseCategoriesForDropdown(browseCategories, activeShopCategorySlug),
    [browseCategories, activeShopCategorySlug],
  );

  useEffect(() => {
    if (isPriceCollectionScope && openId === 'price') {
      setOpenId(null);
    }
  }, [isPriceCollectionScope, openId]);

  function closeCategoryDropdown() {
    setOpenId(null);
  }

  function toggleCategoryDropdown() {
    setOpenId((v) => (v === 'category-breadcrumb' ? null : 'category-breadcrumb'));
  }

  function setMin(v) {
    const x = roundMoney(Number(v));
    if (Number.isNaN(x)) {
      return;
    }
    const upper = clamp(x, safeMin, safeMax);
    const nextMax = Math.max(upper, displayMax);
    setSliderDraft(null);
    onFilterMinChange(upper);
    if (nextMax !== filterMax) {
      onFilterMaxChange(nextMax);
    }
  }

  function setMax(v) {
    const x = roundMoney(Number(v));
    if (Number.isNaN(x)) {
      return;
    }
    const lower = clamp(x, safeMin, safeMax);
    const nextMin = Math.min(lower, displayMin);
    setSliderDraft(null);
    onFilterMaxChange(lower);
    if (nextMin !== filterMin) {
      onFilterMinChange(nextMin);
    }
  }

  function setSliderMin(v) {
    const next = clamp(v, safeMin, safeMax);
    let max = sliderDraft?.max ?? filterMax;
    if (next > max) {
      max = next;
    }
    setSliderDraft({ min: next, max });
  }

  function setSliderMax(v) {
    const next = clamp(v, safeMin, safeMax);
    let min = sliderDraft?.min ?? filterMin;
    if (next < min) {
      min = next;
    }
    setSliderDraft({ min, max: next });
  }

  function pillLabelPrice() {
    if (!hasDomain) {
      return 'Price';
    }
    if (!priceActive) {
      return 'Price';
    }
    return `${fmt.format(displayMin)} – ${fmt.format(displayMax)}`;
  }

  return (
    <div className="filter-bar" ref={rootRef}>
      {browseCategories.length > 0 && (
        <div className="filter-bar__category-nav">
          <nav className="category-breadcrumbs" aria-label="Browse category">
            <Link to="/" className="category-breadcrumbs__crumb category-breadcrumbs__crumb--link">
              Home
            </Link>
            <BreadcrumbSep />
            {activeSubcategory && (
              <>
                <button
                  type="button"
                  className="category-breadcrumbs__crumb category-breadcrumbs__crumb--link"
                  onClick={() => onShopCategoryChange(activeShopCategorySlug)}
                >
                  {activeCategoryName}
                </button>
                <BreadcrumbSep />
              </>
            )}
            <div
              className={`category-breadcrumbs__menu${categoryDropdownOpen ? ' category-breadcrumbs__menu--open' : ''}`}
            >
              <button
                type="button"
                className="category-breadcrumbs__crumb category-breadcrumbs__crumb--dropdown"
                aria-expanded={categoryDropdownOpen}
                aria-haspopup="true"
                aria-label={
                  activeSubcategory ? 'Change subcategory' : 'Change category'
                }
                onClick={toggleCategoryDropdown}
              >
                <span className="category-breadcrumbs__dropdown-label">
                  {!activeShopCategorySlug
                    ? 'Categories'
                    : activeSubcategory
                      ? activeSubcategory.name
                      : activeCategoryName}
                </span>
                <BreadcrumbDropdownIcon />
              </button>
              {categoryDropdownOpen && (
                <div
                  ref={categoryPanelRef}
                  className="filter-pill__panel filter-pill__panel--scroll filter-pill__panel--category category-breadcrumbs__panel"
                  role="dialog"
                  aria-label="Browse categories"
                >
                  <ul className="filter-check-list filter-check-list--stack">
                    {dropdownCategories.map((c) => (
                      <CategoryFilterGroup
                        key={c.slug}
                        category={c}
                        activeShopCategorySlug={activeShopCategorySlug}
                        activeSubcategorySearch={activeSubcategory?.search ?? null}
                        expanded={expandedCategorySlugs.has(c.slug)}
                        forceExpanded={
                          categoryDropdownOpen && c.slug === activeShopCategorySlug
                        }
                        scrollTargetRef={scrollTargetRef}
                        onToggleExpanded={() => {
                          setExpandedCategorySlugs((prev) => {
                            const next = new Set(prev);
                            if (next.has(c.slug)) {
                              next.delete(c.slug);
                            } else {
                              next.add(c.slug);
                            }
                            return next;
                          });
                        }}
                        onShopCategoryChange={onShopCategoryChange}
                        onSubcategoryChange={onSubcategoryChange}
                        onClose={closeCategoryDropdown}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {breadcrumbSearchLabel && (
              <>
                <BreadcrumbSep />
                <span
                  className="category-breadcrumbs__crumb category-breadcrumbs__crumb--current category-breadcrumbs__crumb--search"
                  aria-current="page"
                >
                  {breadcrumbSearchLabel}
                </span>
              </>
            )}
          </nav>
        </div>
      )}
      <div className="filter-bar__pills">
        {!isPriceCollectionScope && (
        <div className={`filter-pill${openId === 'price' ? ' filter-pill--open' : ''}`}>
          <button
            type="button"
            className={`filter-pill__trigger${priceActive ? ' filter-pill__trigger--active' : ''}`}
            aria-expanded={openId === 'price'}
            aria-haspopup="true"
            onClick={() =>
              setOpenId((v) => (v === 'price' ? null : 'price'))
            }
          >
            {pillLabelPrice()}
            <span className="filter-pill__chevron" aria-hidden>
              ▾
            </span>
          </button>
          {openId === 'price' && !hasDomain && (
            <div className="filter-pill__panel" role="dialog" aria-label="Filter by price">
              <p className="filter-pill__empty">No priced products in this list.</p>
            </div>
          )}
          {openId === 'price' && hasDomain && (
            <div
              className="filter-pill__panel"
              role="dialog"
              aria-label="Filter by price"
            >
              <div className="filter-price">
                <div className="filter-price__inputs">
                  <label className="filter-price__field">
                    <span className="filter-price__label">Min</span>
                    <input
                      type="number"
                      className="filter-price__input"
                      min={safeMin}
                      max={safeMax}
                      step={step}
                      value={Number.isFinite(displayMin) ? displayMin : ''}
                      onChange={(e) => setMin(e.target.value)}
                    />
                  </label>
                  <span className="filter-price__dash">–</span>
                  <label className="filter-price__field">
                    <span className="filter-price__label">Max</span>
                    <input
                      type="number"
                      className="filter-price__input"
                      min={safeMin}
                      max={safeMax}
                      step={step}
                      value={Number.isFinite(displayMax) ? displayMax : ''}
                      onChange={(e) => setMax(e.target.value)}
                    />
                  </label>
                </div>
                <div className="filter-price__sliders">
                  <label className="sr-only" htmlFor="filter-price-min-range">
                    Minimum price slider
                  </label>
                  <input
                    id="filter-price-min-range"
                    type="range"
                    className="filter-price__range filter-price__range--min"
                    min={safeMin}
                    max={safeMax}
                    step={step}
                    value={clamp(displayMin, safeMin, safeMax)}
                    onChange={(e) => setSliderMin(Number(e.target.value))}
                  />
                  <label className="sr-only" htmlFor="filter-price-max-range">
                    Maximum price slider
                  </label>
                  <input
                    id="filter-price-max-range"
                    type="range"
                    className="filter-price__range filter-price__range--max"
                    min={safeMin}
                    max={safeMax}
                    step={step}
                    value={clamp(displayMax, safeMin, safeMax)}
                    onChange={(e) => setSliderMax(Number(e.target.value))}
                  />
                </div>
                <p className="filter-price__hint">
                  Range {fmt.format(safeMin)} — {fmt.format(safeMax)}
                </p>
              </div>
            </div>
          )}
        </div>
        )}

        <div className={`filter-pill filter-pill--retailer${openId === 'retailer' ? ' filter-pill--open' : ''}`}>
          <button
            type="button"
            className={`filter-pill__trigger${retailerActive ? ' filter-pill__trigger--active' : ''}`}
            aria-expanded={openId === 'retailer'}
            aria-haspopup="true"
            onClick={() => {
              setOpenId((v) => (v === 'retailer' ? null : 'retailer'));
            }}
          >
            Retailer
            {retailerActive && (
              <span className="filter-pill__badge">{selectedRetailers.size}</span>
            )}
            <span className="filter-pill__chevron" aria-hidden>
              ▾
            </span>
          </button>
          {openId === 'retailer' && (
            <div
              className="filter-pill__panel filter-pill__panel--scroll filter-pill__panel--retailer"
              role="dialog"
              aria-label="Filter by retailer"
            >
              {retailers.length === 0 ? (
                <p className="filter-pill__empty">No retailers in this list.</p>
              ) : (
                <ul className="filter-check-list filter-check-list--stack">
                  {retailerGroups.map((group) => (
                    <li key={group.categoryName} className="filter-check-list__group">
                      <div
                        className="filter-check-list__group-heading"
                        aria-hidden="true"
                      >
                        {group.categoryName}
                      </div>
                      <ul className="filter-check-list__sub-list">
                        {group.retailers.map((name) => (
                          <li key={name} className="filter-check-list__sub-item">
                            <label className="filter-check-list__label filter-check-list__label--sub">
                              <input
                                type="checkbox"
                                checked={selectedRetailers.has(name)}
                                onChange={() => onToggleRetailer(name)}
                              />
                              <span>{name}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <FilterCheckboxPill checked={saleActive} onChange={onSaleChange}>
          Sale
        </FilterCheckboxPill>

        <FilterCheckboxPill checked={onlyInStock} onChange={onOnlyInStockChange}>
          In stock
        </FilterCheckboxPill>

        <FilterViewToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          className="filter-view-toggle--in-bar"
        />
      </div>
    </div>
  );
}
